import axios from 'axios';
import { service } from '../api/axios';

// Mirrors server-side SAS_MAX_SIZE_BYTES. Single source of truth would be a
// shared schemas package; for the cookie-cutover branch we duplicate the
// constant rather than restructure module boundaries.
export const SAS_MAX_SIZE_BYTES = 200 * 1024 * 1024;

interface IssuedUploadSas {
  sasUrl: string;
  blobUrl: string;
  blobName: string;
  expiresAt: string;
}

// Plain axios instance for the storage PUT — must NOT carry credentials,
// auth headers, or our response interceptor (the storage 201 would fire
// the 'Voltooid' toast twice and the 4xx-error handler reads
// `error.response.status` which crashes on network aborts). Storage PUT
// gets its own minimal client.
const blobClient = axios.create();

/**
 * Direct-to-blob upload via Service SAS. Browser PUTs the file body
 * straight to Azure, bypassing the App Service body-size cap (~28.6 MB)
 * and 230 s request timeout that kill the legacy /file-upload-no-library
 * path on big videos. Returns the canonical blob URL (no SAS query string)
 * for storage in Post.fileUrls.
 */
export const uploadFileViaSAS = async (
  file: File,
  onProgress?: (loaded: number, total: number) => void
): Promise<string> => {
  if (file.size > SAS_MAX_SIZE_BYTES) {
    throw new Error(
      `Bestand is te groot (max ${SAS_MAX_SIZE_BYTES / (1024 * 1024)} MB)`
    );
  }

  const sasResponse = await service.post<IssuedUploadSas>('/uploads/sas', {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  });

  const { sasUrl, blobUrl } = sasResponse.data;

  await blobClient.put(sasUrl, file, {
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream',
    },
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        onProgress(event.loaded, event.total);
      }
    },
  });

  return blobUrl;
};
